import {
  Button,
  Form,
  IFormRender,
  Label,
  Link,
  Radio,
  RadioGroupFormField,
  TextFormField,
} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import OnboardingFormCoordinator, {
  IOnboardingFormField,
} from 'containers/onboarding/OnboardingFormCoordinator/OnboardingFormCoordinator';
import { useOpenable, useUsernameAvailable } from 'hooks';
import { Fragment, FunctionComponent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import { v_min_length, v_require, v_unique_username, v_valid_email } from 'utils/validations';
import {
  renderCompanyNameFormField,
  renderConfirmPasswordField,
  renderEmailFormField,
  renderPasswordField,
} from './utils/utils';

export interface IOwnerOnboardingAccountPageProps {
  formData: IOnboardingAccountData;
  onAccountSubmit: (formData: IOnboardingAccountData) => void;
  className?: ClassName;
  disableCaptcha?: boolean;
}

export interface IOnboardingAccountData {
  email: string;
  username?: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  token?: string;
}

const OwnerOnboardingAccountPage: FunctionComponent<IOwnerOnboardingAccountPageProps> = (props) => {
  const { formData, onAccountSubmit, className, disableCaptcha = false } = props;

  const [t] = useTranslation();
  const [dialogFormData, setDialogFormData] = useState({ usernameChoice: 'differentEmail' });
  const usernameTakenOpenable = useOpenable();
  const isUsernameAvailable = useUsernameAvailable();
  const emailInputRef = useRef<HTMLInputElement | null>();

  function focusEmailField() {
    emailInputRef.current?.focus();
  }

  function handleSubmit(formData: IOnboardingAccountData) {
    onAccountSubmit(formData);
  }

  function handleSubmitUsernameTakenDialog(formData: { usernameChoice: string }) {
    setDialogFormData(formData);
    usernameTakenOpenable.close();
    if (formData.usernameChoice === 'differentEmail') {
      focusEmailField();
    }
  }

  function renderUsernameFormField(form: any, onFocus: () => void, onBlur: () => void) {
    function shouldValidateOnUsernameUpdate(previousFormData: IOnboardingAccountData) {
      return previousFormData.username !== form.state.data.username;
    }

    return (
      <Fragment key="username">
        <Label secondaryLabel={t('Username must be at least 2 characters')}>{t('Username')}</Label>
        <TextFormField
          name={'username'}
          onFocus={onFocus}
          onBlur={onBlur}
          form={form}
          shouldValidateOnUpdate={shouldValidateOnUsernameUpdate}
          validations={[
            { validation: v_require },
            { validation: v_unique_username },
            { validation: v_min_length, args: { min: 2, message: t('Minimum 2 characters') } },
          ]}
          autofocus={dialogFormData.usernameChoice === 'separateUsername'}
        />
      </Fragment>
    );
  }

  function renderEmailFormFieldAsUsernameField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="email">
        <Label>{t('Email (Username)')}</Label>
        <TextFormField
          name="email"
          onFocus={onFocus}
          onBlur={onBlur}
          validations={[{ validation: v_require }, { validation: v_valid_email }, { validation: v_unique_username }]}
          form={form}
          inputRef={(ref) => (emailInputRef.current = ref)}
        />
      </Fragment>
    );
  }

  const AccountPageFormFieldsWithUsername: IOnboardingFormField[] = [
    {
      panelContent: {
        bodyText: `${t('Example')}: ${t('Johnson Excavation, LLC')}`,
        headerText: t('What is the legal name of your company?'),
        isTitle: false,
      },
      renderField: renderCompanyNameFormField,
    },
    {
      panelContent: {
        bodyText: '',
        headerText: t('Please enter a valid email address.'),
        isTitle: false,
      },
      renderField: renderEmailFormField,
    },
    {
      panelContent: {
        bodyText: t('Your username will not be case sensitive.'),
        headerText: t('What username would you like to use?'),
        isTitle: false,
      },
      renderField: renderUsernameFormField,
    },
    {
      panelContent: {
        bodyText: t(
          'Your password must be at least 8 characters long and may be any combination of letters, numbers, or symbols. Remember, a longer password will always be more secure. Your password is case sensitive.'
        ),
        headerText: t('What is your password?'),
        isTitle: false,
      },
      renderField: renderPasswordField,
    },
    {
      panelContent: {
        bodyText: t(
          'Your password must be at least 8 characters long and may be any combination of letters, numbers, or symbols. Remember, a longer password will always be more secure. Your password is case sensitive.'
        ),
        headerText: t('What is your password?'),
        isTitle: false,
      },
      renderField: renderConfirmPasswordField,
    },
  ];

  const AccountPageFormFieldsWithoutUsername: IOnboardingFormField[] = [
    {
      panelContent: {
        bodyText: `${t('Example')}: ${t('Johnson Excavation, LLC')}`,
        headerText: t('What is the legal name of your company?'),
        isTitle: false,
      },
      renderField: renderCompanyNameFormField,
    },
    {
      panelContent: {
        bodyText: '',
        headerText: t('Please enter a valid email address.'),
        isTitle: false,
      },
      renderField: renderEmailFormFieldAsUsernameField,
    },
    {
      panelContent: {
        bodyText: t(
          'Your password must be at least 8 characters long and may be any combination of letters, numbers, or symbols. Remember, a longer password will always be more secure. Your password is case sensitive.'
        ),
        headerText: t('What is your password?'),
        isTitle: false,
      },
      renderField: renderPasswordField,
    },
    {
      panelContent: {
        bodyText: t(
          'Your password must be at least 8 characters long and may be any combination of letters, numbers, or symbols. Remember, a longer password will always be more secure. Your password is case sensitive.'
        ),
        headerText: t('What is your password?'),
        isTitle: false,
      },
      renderField: renderConfirmPasswordField,
    },
  ];

  function getFormFields(): IOnboardingFormField[] {
    return dialogFormData.usernameChoice === 'separateUsername'
      ? AccountPageFormFieldsWithUsername
      : AccountPageFormFieldsWithoutUsername;
  }

  function renderFormFooter() {
    return (
      <div className="mt-4 f-sm">
        {t('By creating an account, you agree to the')}
        <Link href="https://busybusy.com/user-agreement/" newTab={true}>
          {' '}
          {t('terms and agreements')}{' '}
        </Link>
        {t('and')}
        <Link href="https://busybusy.com/privacy/" newTab={true}>
          {' '}
          {t('privacy policy')}.
        </Link>
      </div>
    );
  }

  function onSubmissionFailure(formData: IOnboardingAccountData) {
    if (dialogFormData.usernameChoice !== 'separateUsername') {
      isUsernameAvailable(formData.email).then((isAvailable: boolean) => {
        if (isAvailable === false) {
          usernameTakenOpenable.open();
        }
      });
    }
  }

  function formRender(form: IFormRender<any>) {
    function onFormSubmitMouseDown(e: any) {
      e.preventDefault();
    }

    function handleOnClick() {
      form.handleSubmit();
    }

    return (
      <>
        <RadioGroupFormField form={form} name="usernameChoice">
          <Radio value="differentEmail" label={t('Use Different Email')} />
          <Radio value="separateUsername" label={t('Use Separate Username')} />
        </RadioGroupFormField>
        <Button className={'form-button'} type={'primary'} onMouseDown={onFormSubmitMouseDown} onClick={handleOnClick}>
          {t('Submit')}
        </Button>
      </>
    );
  }

  const classes = classNames(
    {
      'owner-onboarding-account-page': true,
    },
    className
  );

  return (
    <>
      <OnboardingFormCoordinator
        className={classes}
        defaultHeader={t('Account')}
        defaultBody={t(`To get started, let’s create your personal account`)}
        formFields={getFormFields()}
        formData={formData}
        formFooter={renderFormFooter()}
        onSubmit={handleSubmit}
        formButtonLabel={t('Create Account')}
        onSubmissionFailure={onSubmissionFailure}
        dialogFormData={dialogFormData}
        disableCaptcha={disableCaptcha}
      />
      <HeaderDialog
        isOpen={usernameTakenOpenable.isOpen}
        onClose={usernameTakenOpenable.close}
        title={t('Username Taken')}
        divider={false}
      >
        <div className={'pb-6 px-6'}>
          <h4 className={'pb-6'}>
            {t(
              'This email already exists as a username for another account. You can use a different email or create a non-email username.'
            )}
          </h4>
          <Form
            data={dialogFormData}
            onSubmit={handleSubmitUsernameTakenDialog}
            render={formRender}
            allowMultipleSubmissions={true}
          />
        </div>
      </HeaderDialog>
    </>
  );
};

export default OwnerOnboardingAccountPage;
